import type { CSSProperties } from 'react';
import type { BreakRequestsTheme } from './types';

/**
 * Style buckets for {@link BreakDecisionModal} and its inline sub-components.
 * Building inline styles from a theme is the same pattern
 * {@link CounterOfferModal} uses — keeps the shared widget surface-agnostic
 * (no global CSS) while letting each consumer supply its own palette via
 * {@link BreakRequestsTheme}.
 */
export interface BreakDecisionModalStyles {
  readonly backdrop: CSSProperties;
  readonly panel: CSSProperties;
  readonly title: CSSProperties;
  readonly headerMeta: CSSProperties;
  readonly mutedLine: CSSProperties;
  readonly sectionHeading: CSSProperties;
  readonly sectionList: CSSProperties;
  readonly optionRow: CSSProperties;
  readonly optionBody: CSSProperties;
  readonly optionHeading: CSSProperties;
  readonly optionRationale: CSSProperties;
  readonly optionDigest: CSSProperties;
  readonly optionWarning: CSSProperties;
  readonly composerGrid: CSSProperties;
  readonly fieldLabel: CSSProperties;
  readonly fieldCaption: CSSProperties;
  readonly input: CSSProperties;
  readonly textarea: CSSProperties;
  readonly validationLine: CSSProperties;
  readonly composerActions: CSSProperties;
  readonly rowActions: CSSProperties;
  readonly footer: CSSProperties;
  readonly ghostButton: CSSProperties;
  readonly secondaryButton: CSSProperties;
  readonly primaryButton: CSSProperties;
}

export function buildBreakDecisionModalStyles(theme: BreakRequestsTheme): BreakDecisionModalStyles {
  const input: CSSProperties = {
    background: theme.colors.bgTertiary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.xs,
    color: theme.colors.textPrimary,
    fontSize: theme.fontSize.sm,
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    outline: 'none',
  };
  const baseButton: CSSProperties = {
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.xs,
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    fontSize: theme.fontSize.sm,
    fontWeight: 500,
    cursor: 'pointer',
    transition: `all ${theme.transitions.fast}`,
  };
  return {
    backdrop: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    panel: {
      background: theme.colors.bgSecondary,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.radius.sm,
      padding: theme.spacing.md,
      width: 'min(640px, 92vw)',
      maxHeight: '92vh',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.sm,
    },
    title: {
      margin: 0,
      fontSize: theme.fontSize.md,
      color: theme.colors.textPrimary,
      fontWeight: 600,
    },
    headerMeta: {
      fontSize: theme.fontSize.sm,
      color: theme.colors.textPrimary,
    },
    mutedLine: {
      fontSize: theme.fontSize.xs,
      color: theme.colors.textMuted,
    },
    sectionHeading: {
      fontSize: theme.fontSize.xs,
      fontWeight: 600,
      color: theme.colors.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      marginTop: theme.spacing.xs,
    },
    sectionList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.xs,
    },
    optionRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing.sm,
      padding: theme.spacing.sm,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.radius.xs,
      background: theme.colors.bgTertiary,
    },
    optionBody: {
      flex: '1 1 auto',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    optionHeading: {
      fontSize: theme.fontSize.sm,
      fontWeight: 600,
      color: theme.colors.textPrimary,
    },
    optionRationale: {
      fontSize: theme.fontSize.xs,
      color: theme.colors.textSecondary,
      fontStyle: 'italic',
    },
    optionDigest: {
      fontSize: theme.fontSize.xs,
      color: theme.colors.textMuted,
    },
    // Coverage-gap detail lines are always shortfalls — color them red so
    // the leading `⚠` glyph (text-presentation form) reads as a warning
    // instead of neutral text. Matches `BreakRequestRow`'s Row 2 styling.
    optionWarning: {
      fontSize: theme.fontSize.xs,
      color: '#ef4444',
    },
    composerGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: theme.spacing.sm,
    },
    fieldLabel: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    },
    fieldCaption: {
      fontSize: theme.fontSize.xs,
      color: theme.colors.textMuted,
    },
    input,
    textarea: {
      ...input,
      resize: 'vertical',
      minHeight: 56,
      fontFamily: 'inherit',
    },
    validationLine: {
      fontSize: theme.fontSize.xs,
      color: theme.colors.error,
    },
    composerActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing.xs,
    },
    rowActions: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.xs,
      flexShrink: 0,
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing.xs,
      marginTop: theme.spacing.xs,
    },
    ghostButton: {
      ...baseButton,
      background: theme.colors.bgTertiary,
      color: theme.colors.textSecondary,
    },
    secondaryButton: {
      ...baseButton,
      background: theme.colors.bgSecondary,
      color: theme.colors.textPrimary,
      borderColor: theme.colors.textSecondary,
    },
    primaryButton: {
      ...baseButton,
      background: theme.colors.primary,
      color: theme.colors.primaryButtonText,
      borderColor: theme.colors.primary,
    },
  };
}
